<template>
  <div>
    <reports-title
      ref="reportsTitle"
      :title="pageTitle"
    />
    <reports-toolbar
      ref="reportsToolbar"
      :drop-menu="modelsOfCalculationsMenu"
      :active-report-view-mode="reportViewMode"
      :report-title="pageTitle"
      :range-value="toolbarRange"
      :toolbar-disabled="isStatic || isDisabled"
      :selected-group-list="toolbarSelectedGroupsList"
      :group-list="toolbarGroupsList"
      @onSelectDropdown="onChangeCalculationMode"
      @onSelectReportViewMode="onSelectReportViewMode"
      @onClickExpand="toggleAllGroups(true)"
      @onClickCollapse="toggleAllGroups(false)"
      @onSelectGroup="onSelectGroup"
      @onClickExport="onExport"
    />
    <skeleton-wrapper
      v-if="isShowSkeleton"
      id="skeletonWrapper"
      :style="{ height: contentHeight }"
    >
      <template #body>
        <vertical-bar-chart
          v-if="reportViewMode === 'graphic'"
          :stub-config="reportChartConfigs.stub"
          :is-static="true"
          :custom-legends="true"
          :show-stub-message="false"
        />
        <skeletonTable v-if="reportViewMode === 'table'" />
      </template>
    </skeleton-wrapper>
    <div v-else>
      <vertical-bar-chart
        v-if="reportViewMode === 'graphic'"
        id="report_export_id"
        ref="chart"
        :config="chartConfig"
        :stub-config="reportChartConfigs.stub"
        :is-static="isStatic"
        :custom-legends="true"
        @onAllLegendsDisabled="flag => isDisabled = flag"
      />
      <report-table
        v-if="reportViewMode === 'table'"
        ref="table"
        :config="tableConfig"
        :rows-data="filteredTableData"
        :grouping="grouping"
        :expanded="expanded"
        :expand-settings="expandSettings"
        :local-storage-table-id="currentFilter.type"
        :skin="'horizontal-borders'"
        @saveExpanded="saveExpanded"
        :style="{ height: contentHeight }"
      />
    </div>
  </div>
</template>

<script>
import app from '$$app';
import reportsTitle from '../include/title.vue';
import reportsToolbar from '../include/toolbar.vue';
import verticalBarChart from '../include/charts/verticalBarChart.vue';
import constants from '$$helpers/constants';
import multiViewsProjects from '$$multiviewModel';
import timeReportConfigs from '../time/configCharts';
import budgetReportConfigs from '../budget/configCharts';
import ProjectsTimeReport from '../time/controllerReportByProjects';
import ProjectsBudgetReport from '../budget/controllerReportByProjects';
import reportsConstants from '../include/constants';
import projectsModel from '../../../models/projects';
import skeletonWrapper from '../../common/VueComponents/skeletons/skeletonWrapper.vue';
import statusHelper from '../../../helpers/status';
import reportTable from '../include/table/table.vue';
import budgetTableConfigs from '../budget/configTable';
import timeTableConfigs from '../time/configTable';
import reportHelpers from '../include/helpers';
import skeletonTable from '../include/table/skeleton.vue';
import Export from '../include/export/export';
import Filter from '../../filter/filter';
import tableHelpers from '../include/table/helpers';

export default {
  name: 'ReportByProjectsMainPage',
  components: {
    reportsTitle, reportsToolbar, verticalBarChart, skeletonWrapper, reportTable, skeletonTable,
  },
  props: {
    reportType: { type: String, required: true, default: 'budget' },
  },
  data() {
    return {
      appEvents: [],
      locale: __,
      projectIds: [],
      multiviewProjectIds: [],
      baselines: [],
      baselineData: {},
      baselineId: null,
      chartConfig: null,
      tableData: [],
      reportViewMode: '',
      grouping: ['project'],
      expanded: null,
      calculationMode: constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED,
      toolbarRange: '',
      isStatic: true,
      isShowSkeleton: true,
      isDisabled: false,
      expandSettings: {
        key: 0,
        isAllExpanded: true,
      },
      contentHeight: '0px',
    };
  },
  computed: {
    pageTitle() {
      if (this.$route?.name === 'timeReports') {
        return reportsConstants.TITLE.time.timeByProjects;
      }
      if (this.$route?.name === 'budgetReports') {
        return reportsConstants.TITLE.budget.budgetByProjects;
      }

      return '';
    },
    modelsOfCalculationsMenu() {
      return reportHelpers.getModelsOfCalculationsMenu(this.isStatic, this.calculationMode, this.baselines, this.baselineId);
    },
    currentFilter() {
      return this.$store.getters['filter/currentFilter'];
    },
    baselineTasks() {
      if (this.baselineData?.tasks) {
        const baselineTasks = this.baselineData.tasks;

        return baselineTasks;
      }

      return [];
    },
    reportChartConfigs() {
      let configs = {};

      if (this.reportType === 'budget') {
        configs = { ...budgetReportConfigs.projectsReport() };
      }

      if (this.reportType === 'time') {
        configs = { ...timeReportConfigs.projectsReport() };
      }

      return configs;
    },
    currentProjectIds() {
      return [this.projectIds, this.multiviewProjectIds].find(arr => arr.length) || [];
    },
    tableConfig() {
      let config = null;

      if (this.reportType === 'budget') {
        config = budgetTableConfigs.budgetByProjects;
      }

      if (this.reportType === 'time') {
        config = timeTableConfigs.timeByProjects;
      }

      if (config) {
        return tableHelpers.getColumnVisibilityByCalculationMode(config, this.calculationMode);
      }

      return {
        columns: [],
        state: {},
      };
    },
    tableGroupsList() {
      return [
        { type: 'project', label: __('filter_option_gantt_id_label') },
        { type: 'assignee', label: __('filter_option_assignee_label') },
      ];
    },
    toolbarGroupsList() {
      return this.tableGroupsList.filter(item => !this.grouping.includes(item.type));
    },
    toolbarSelectedGroupsList() {
      return this.tableGroupsList.filter(item => this.grouping.includes(item.type));
    },
    filteredTableData() {
      const { text, assignee } = this.currentFilter.properties;
      let filtered = tableHelpers.changeTableDataByGroups(this.grouping, this.tableData);

      filtered = reportHelpers.returnTasksByFilterText(filtered, text);
      filtered = reportHelpers.returnTasksByFilterAssignee(filtered, assignee);

      return filtered;
    },
    sourceType() {
      if (this.multiviewProjectIds.length) {
        return 'multiview';
      }

      return 'projects';
    },
    baselineEnabledByCalculationMode() {
      const baselineEnabledByCalculationMode = [
        constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE, constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE
      ].includes(this.calculationMode)

      return baselineEnabledByCalculationMode;
    }
  },
  watch: {
    'currentFilter.properties': {
      handler(newProps, oldProps) {
        if (!newProps && !oldProps) return;

        this.calculateReportByFilter(newProps, oldProps)
      },
      deep: true,
      immediate: true,
    },
    isShowSkeleton(flag) {
      if (!flag && this.tableData.length && this.reportViewMode === 'table') {
        this.toggleAllGroups(true);
      }
    },
    reportViewMode(newVal) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          mode: newVal,
        },
      });
    },
    calculationMode(newVal) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          calculationModel: newVal,
        },
      });
    },
  },
  created() {
    this.init();
  },
  beforeUnmount() {
    this.appEvents.forEach(event => app.off(event));
    window.removeEventListener('resize', this.calcContentHeight);
  },
  mounted() {
    const reportNameAnalytics = this.pageTitle.replace(/^exmp_chart_/, '').replace(/_title$/, '');

    userExtAnalytics.log('report_open', {
      report_type: reportNameAnalytics,
      calculation_mode: this.calculationMode,
      source_type: this.sourceType,
      projects_selected_count: this.currentProjectIds.length,
    });

    const onAfterTimeCalculation = app.on('onAfterProjectsTimeReportCalculation', result => {
      this.setReportResult(result);
    });
    const onAfterBudgetCalculation = app.on('onAfterProjectsBudgetCalculation', result => {
      this.setReportResult(result);
    });

    this.appEvents.push(onAfterTimeCalculation, onAfterBudgetCalculation);
    this.calcContentHeight();
    window.addEventListener('resize', this.calcContentHeight);
  },
  methods: {
    calcContentHeight() {
      const viewportHeight = document.documentElement.clientHeight || window.innerHeight || 0;

      const titleRef = this.$refs.reportsTitle;
      const toolbarRef = this.$refs.reportsToolbar;

      const titleEl = titleRef && (titleRef.$el || titleRef);
      const toolbarEl = toolbarRef && (toolbarRef.$el || toolbarRef);

      const titleHeight = titleEl?.getBoundingClientRect().height || 0;
      const toolbarHeight = toolbarEl?.getBoundingClientRect().height || 0;

      const height = Math.max(viewportHeight - titleHeight - toolbarHeight, 0);

      this.contentHeight = `${height}px`;
    },
    init() {
      if (this.reportType === 'budget') {
        ProjectsBudgetReport.initCostWorker();
      }

      if (this.reportType === 'time') {
        ProjectsTimeReport.initTimeWorker();
      }

      this.chartConfig = { ...this.reportChartConfigs.stub };
      this.calculationMode = this.$route?.query?.calculationModel || constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED;
      this.reportViewMode = this.$route?.query?.mode || 'graphic';

      this.calculateReportByFilter(this.currentFilter?.properties)
    },

    async calculateReportByFilter(newProps, oldProps = null) {
      Filter.watchChangedFilterProps(newProps, oldProps, {
        gantt_id: this.setGanttIds,
        portfolio_id: this.setMultiviewProjectIds,
        rangeDate: this.setRange,
      });

      const ids = [this.projectIds, this.multiviewProjectIds].find(arr => arr.length) || [];

      if (!ids.length) {
        this.projectIds = projectsModel.getAllProjects().map(project => project.gantt_id);

        const chartComponent = this.$refs.chart;

        if (chartComponent && chartComponent.instance) {
          chartComponent.instance.dispatchAction({
            type: 'legendAllSelect',
          });
        }
      }

      if (this.currentProjectIds?.length) {
        await this.getBaselines();

        this.updateFilterAndCalculationMode();
        this.calculateReportByWorker();
      } else {
        setTimeout(() => {
          this.isStatic = true;
          this.chartConfig = { ...this.reportChartConfigs.stub };
        }, 0);
      }
    },

    calculateReportByWorker() {
      if (this.reportType === 'budget') {
        ProjectsBudgetReport.calculateCosts(this.currentProjectIds, this.baselineTasks, this.calculationMode);
      }

      if (this.reportType === 'time') {
        ProjectsTimeReport.calculateTime(this.currentProjectIds, this.baselineTasks, this.calculationMode);
      }
    },

    recalculateReport() {
      if (this.reportType === 'budget') {
        ProjectsBudgetReport.calculationMode = this.calculationMode;
        ProjectsBudgetReport.calculateProjectsBudgetReport();
      }

      if (this.reportType === 'time') {
        ProjectsTimeReport.calculationMode = this.calculationMode;
        ProjectsTimeReport.createReportResult();
      }
    },

    setGanttIds(newVal) {
      if (newVal?.length) {
        this.multiviewProjectIds = [];
        this.projectIds = [...newVal];
      } else {
        this.projectIds = [...newVal];
      }
    },

    setMultiviewProjectIds(newVal) {
      if (newVal?.length) {
        const multiviewData = multiViewsProjects.getProjectDataById(newVal[0]);

        this.projectIds = [];
        this.multiviewProjectIds = [...multiviewData.ganttIDs];
      } else {
        this.multiviewProjectIds = [];
      }
    },

    setRange() {
      this.toolbarRange = reportHelpers.setToolbarRangeValue(this.currentFilter);
    },

    async getBaselines() {
      this.baselineId = null;
      this.baselines = [];
      this.baselineData = {};

      if (this.currentProjectIds.length === 1) {
        await this.$store.dispatch('baselines/fetchBaselines', this.currentProjectIds[0]);

        const baselines = this.$store.getters['baselines/getProjectBaselines'](this.currentProjectIds[0])
          .map(item => ({ ...item, created_at: new Date(item.created_at) }))
          .sort((a, b) => a.created_at - b.created_at);

        if (baselines?.length) {
          this.baselines = baselines;
          this.baselineId = baselines[baselines.length - 1].id;
          await this.getBaselineData(this.baselineId);
        }
      }
    },

    async getBaselineData(baselineId) {
      const baseline = this.baselines.find(b => b.id === +baselineId);

      if (!baseline) {
        this.baselineData = {};

        return
      }

      const data = await this.$store.dispatch('baselines/getBaselineData', { projectId: baseline.gantt_id, eventId: baseline.event_id });

      if (data) {
        this.baselineData = data;
        this.baselineId = baselineId;
      }
    },

    setReportResult(result) {
      if (result) {
        this.isStatic = false;
        this.tableData = result.table;
        this.chartConfig = { ...result.chart };
      } else {
        setTimeout(() => {
          this.isStatic = true;
          this.chartConfig = { ...this.reportChartConfigs.stub };
          this.tableData = [];
          this.baselines = [];
        }, 0);
      }

      setTimeout(() => {
        this.isShowSkeleton = false;
      }, 500)
      statusHelper.handlers.hideMainSpinner();
    },

    async onChangeCalculationMode(selected) {
      statusHelper.handlers.showMainSpinner();
      const reportNameAnalytics = this.pageTitle.replace(/^exmp_chart_/, '').replace(/_title$/, '');

      userExtAnalytics.log('report_calculation_mode_change', {
        report_type: reportNameAnalytics,
        from: this.calculationMode,
        to: selected.type,
      });

      this.calculationMode = selected.type;

      let baselineEnabled = false;

      if (selected.baselineId) {
        await this.getBaselineData(selected.baselineId);
        this.calculateReportByWorker();
        baselineEnabled = true;
      } else {
        this.baselineId = null;
        this.recalculateReport();
      }

      this.updateFilterAdvancedSettings({
        baselineEnabled: baselineEnabled,
      })
    },

    onSelectReportViewMode(data) {
      this.reportViewMode = data.type;
      this.updateFilterAdvancedSettings({
        mode: data.type,
      })
    },

    onSelectGroup(data) {
      this.grouping = this.grouping.includes(data.type)
        ? this.grouping.filter(g => g !== data.type)
        : [...this.grouping, data.type];
    },

    toggleAllGroups(flag) {
      this.expandSettings = {
        key: ++this.expandSettings.key,
        isAllExpanded: flag,
      };
    },

    onExport() {
      const analyticTypeName = `${this.reportType}_variance_by_project`;
      const analyticTypeFormat = this.reportViewMode === 'table' ? 'XLSX' : 'PDF';

      if (this.reportViewMode === 'table') {
        Export.exportTanstackTableDataToExcel({
          title: __(`${this.pageTitle}`),
          tableInstance: this.$refs.table?.tableController?.tableInstance,
          aggregatedColumns: this.tableConfig.aggregatedColumns,
          nameTaskColumnKey: 'task_name',
          reportType: this.reportType,
        });
      }

      if (this.reportViewMode === 'graphic') {
        Export.exportReportGraphicToPdf({
          title: __(`${this.pageTitle}`),
          range: this.toolbarRange,
        });
      }

      userExtAnalytics.log('report_export_done', {
        export_format: analyticTypeFormat,
        report_type: analyticTypeName,
      });
    },

    saveExpanded(expanded) {
      this.expanded = expanded;
    },

    updateFilterAndCalculationMode() {
      if (!this.baselineEnabledByCalculationMode && this.projectIds.length > 1) {
        this.updateFilterAdvancedSettings({
          isShowNotificationMessage: false,
        })
      }

      if (this.baselineEnabledByCalculationMode && this.projectIds.length > 1) {
        this.resetCalculationMode();
        this.updateFilterAdvancedSettings();
      }

      if (this.baselineEnabledByCalculationMode && !this.baselines.length) {
        this.resetCalculationMode();
        this.updateFilterAdvancedSettings({
          baselineEnabled: false,
          isShowNotificationMessage: true,
        })
      }

      if (this.baselineEnabledByCalculationMode) {
        this.updateFilterAdvancedSettings({
          baselineEnabled: true,
          isShowNotificationMessage: false,
        })
      }
    },

    updateFilterAdvancedSettings(settings = {}) {
      const defaultFilterSettings = {
        type: this.currentFilter.type,
        settings: {
          baselineEnabled: false,
          isShowNotificationMessage: false,
        }
      }

      this.$store.commit('filter/updateAdvancedSettingsByFilterType', {
        ...defaultFilterSettings,
        settings: {
          ...defaultFilterSettings.settings,
          ...settings
        }
      });
    },

    resetCalculationMode() {
      this.calculationMode = constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED;
      ProjectsBudgetReport.calculationMode = constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED;
      ProjectsTimeReport.calculationMode = constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED;
    }
  },
};
</script>
